import React, { useMemo, useCallback, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  PenLine,
  BookOpen,
  BookMarked,
  Target,
  Shield,
  Menu,
  Moon,
  Sun,
  Sparkles,
  Users,
  CheckCircle2,
  Brain,
  ChevronRight,
  Globe,
  ChevronDown,
  Check,
} from "lucide-react";

import { Button } from "../../components/ui/Button";
import { ROUTES } from "../../utils/constants";
import { useTheme } from "../../contexts/ThemeContext";
import { Section } from "../../components/marketing/Section";
import { SplitFeature } from "../../components/marketing/SplitFeature";
import { IconGrid, type IconGridItem } from "../../components/marketing/IconGrid";
import { Tabs, type TabItem } from "../../components/marketing/Tabs";
import { StepTimeline, type StepItem } from "../../components/marketing/StepTimeline";
import { TestimonialSlider, type TestimonialItem } from "../../components/marketing/TestimonialSlider";
import { MetricCard } from "../../components/landing/MetricCard";
import type { EffectiveBranding } from "../../modules/platform/api/branding.types";
import { resolveBrandingAssetUrl } from "../../modules/platform/api/branding.types";
import { setPublicTenantIntent } from "../../utils/publicTenantHandoff";
import {
  MALAY_LANDING_LANGUAGE_OPTIONS,
  malayLandingLanguageDisplayCode,
  malayLandingLanguageFullLabel,
} from "./malayLandingLanguageLabels";

interface MalayLandingPageProps {
  branding?: EffectiveBranding;
}

type LangMode = "ms" | "en";

const TEXT = {
  ms: {
    brandDefault: "Marshall Cavendish Education",
    brandSubtitle: "Pendidikan Marshall Cavendish",
    heroTitle: "Pembelajaran Bahasa Melayu yang lebih menarik, diperibadikan dan bermakna.",
    heroSubtitle:
      "Platform pembelajaran Bahasa Melayu berkuasa AI yang menghubungkan pembelajaran di sekolah dengan latihan di rumah secara selamat dan berkesan.",

    login: "Mula Belajar",
    adminLogin: "Log Masuk Pentadbir",
    request: "Daftar Akses Sekolah",

    nav: {
      how: "Cara Berfungsi",
      modules: "Modul Utama",
      tutor: "Cikgu Alin (AI)",
      writing: "Penulisan & Membaca",
      progress: "Kemajuan Murid",
      teachers: "Guru & Pentadbir",
      safety: "Keselamatan",
    },
    trust: {
      title: "Dipercayai oleh Sekolah & Keluarga",
      item1: "Bahasa Melayu Baku",
      item2: "Kurikulum Unit 5",
      item3: "Bimbingan AI Cikgu Alin",
    },
    how: {
      title: "Empat Langkah Mudah Pembelajaran Bahasa Melayu",
      subtitle: "Bagaimana murid membina kemahiran berbahasa Melayu secara yakin bersama Cikgu Alin.",
      steps: [
        {
          step: 1,
          title: "Pilih Gred & Modul",
          desc: "Pilih tahap gred dan modul pembelajaran seperti Unit 5, Penulisan Naratif, atau Pemahaman.",
        },
        {
          step: 2,
          title: "Latihan Bersama Tutor AI",
          desc: "Cikgu Alin memberikan soalan interaktif, senario perbualan, dan bimbingan tatabahasa.",
        },
        {
          step: 3,
          title: "Respons Suara atau Teks",
          desc: "Murid menjawab menggunakan input suara atau penaip teks; sistem memberikan maklum balas serta-merta.",
        },
        {
          step: 4,
          title: "Jejak Kemajuan & Can-Do",
          desc: "Pantau penguasaan perbendaharaan kata, pencapaian kemahiran Can-Do, dan laporan guru.",
        },
      ],
    },
    modulesGrid: [
      {
        title: "AI Malay Tutor (Cikgu Alin)",
        desc: "Latihan perbualan interaktif dan bimbingan Bahasa Melayu Baku secara personal.",
        bullets: [
          "Sesi perbualan Bahasa Melayu Baku",
          "Maklum balas sebutan & tatabahasa mesra murid",
          "Senario perbualan mengikut konteks kehidupan harian",
        ],
      },
      {
        title: "Pembelajaran Unit 5",
        desc: "Pelajaran terstruktur kurikulum termasuk tatabahasa 'ialah/adalah' dan penyampaian idea.",
        bullets: [
          "Penguasaan tatabahasa khas (ialah / adalah)",
          "Menerangkan idea dengan alasan sokongan yang jelas",
          "Pengukuhan ungkapan & kosa kata Unit 5",
        ],
      },
      {
        title: "Pemahaman Membaca (Reading)",
        desc: "Latihan membaca petikan interaktif, konteks kosa kata, dan soalan refleksi.",
        bullets: [
          "Petikan fiksyen & bukan fiksyen mengikut tahap",
          "Analisis kosa kata & perbandingan petikan",
          "Soalan pemahaman berasaskan refleksi & kognitif",
        ],
      },
      {
        title: "Penulisan Naratif (Narrative Writing)",
        desc: "Bimbingan AI untuk perancangan watak, latar, konflik, dan urutan karangan.",
        bullets: [
          "Perancangan watak utama & watak sampingan",
          "Pembinaan konflik & urutan peristiwa karangan",
          "Maklum balas penulisan & cadangan penambahbaikan",
        ],
      },
    ],
    vocabulary: {
      title: "Perbendaharaan Kata & Ungkapan Bahasa Melayu",
      subtitle: "Kembangkan penguasaan kata, sinonim, antonim, dan frasa harian.",
      tabs: {
        dictionary: {
          label: "Kosa Kata Konteks",
          text: "Pelajari makna kata Bahasa Melayu Baku beserta contoh ayat yang jelas dan relevan.",
        },
        flashcards: {
          label: "Kad Imbasan Interaktif",
          text: "Latihan pantas kad imbasan berserta audio sebutan untuk ingatan jangka panjang.",
        },
        wordOfDay: {
          label: "Latihan ialah / adalah",
          text: "Latihan tatabahasa khas bagi penggunaan kata pemeri 'ialah' dan 'adalah' dalam ayat.",
        },
      },
    },
    testimonials: {
      title: "Kata Pendidik & Ibu Bapa",
      subtitle: "Kisah kejayaan pembelajaran Bahasa Melayu menggunakan platform ini.",
      items: [
        {
          quote: "Cikgu Alin sangat membantu murid saya berlatih perbualan Bahasa Melayu dengan yakin di dalam kelas.",
          author: "Cikgu Mariam Binti Ahmad",
          role: "Guru Bahasa Melayu",
        },
        {
          quote: "Modul Penulisan Naratif membantu anak saya menyusun jalan cerita dengan lebih berstruktur dan menarik.",
          author: "Encik Farhan",
          role: "Ibu Bapa",
        },
        {
          quote: "Laporan kemajuan Can-Do membolehkan kami memantau perkembangan perbendaharaan kata murid setiap minggu.",
          author: "Puan Zaiton",
          role: "Pentadbir Sekolah",
        },
      ],
    },
    safety: {
      title: "Keselamatan & Privasi Murid",
      subtitle: "Platform yang selamat, mesra murid, dan memenuhi piawaian privasi.",
      items: ["Patuh COPPA & GDPR", "Persekitaran Mesra Kanak-Kanak", "Privasi Data Terjamin"],
      body: "Maklumat murid dan aktiviti pembelajaran dilindungi sepenuhnya. Kami tidak menjual data peribadi murid.",
    },
    footer: {
      ctaTitle: "Bersedia Untuk Mulakan Pembelajaran Bahasa Melayu?",
      ctaSubtitle: "Log masuk sekarang atau minta akses sekolah anda.",
      copyright: "Marshall Cavendish Education — Platform Pembelajaran Bahasa Melayu. Hak Cipta Terpelihara.",
    },
  },
  en: {
    brandDefault: "Marshall Cavendish Education",
    brandSubtitle: "Marshall Cavendish Education",
    heroTitle: "Make Bahasa Melayu learning more engaging, personalised and meaningful.",
    heroSubtitle:
      "An AI-powered Bahasa Melayu learning platform connecting classroom learning with practice at home safely and effectively.",

    login: "Start Learning",
    adminLogin: "Admin Login",
    request: "Request School Access",

    nav: {
      how: "How It Works",
      modules: "Core Modules",
      tutor: "Cikgu Alin (AI)",
      writing: "Writing & Reading",
      progress: "Student Progress",
      teachers: "Teachers & Admins",
      safety: "Safety",
    },
    trust: {
      title: "Trusted by Schools & Families",
      item1: "Standard Bahasa Melayu",
      item2: "Unit 5 Curriculum",
      item3: "AI Guidance by Cikgu Alin",
    },
    how: {
      title: "Four Simple Steps to Learn Bahasa Melayu",
      subtitle: "How students build confidence in Bahasa Melayu with Cikgu Alin.",
      steps: [
        {
          step: 1,
          title: "Select Grade & Module",
          desc: "Choose the grade level and module such as Unit 5, Narrative Writing, or Reading Comprehension.",
        },
        {
          step: 2,
          title: "Practice with AI Tutor",
          desc: "Cikgu Alin provides interactive questions, conversation scenarios, and grammar guidance.",
        },
        {
          step: 3,
          title: "Respond by Voice or Text",
          desc: "Students answer using voice or text input; receiving instant feedback.",
        },
        {
          step: 4,
          title: "Track Progress & Can-Do",
          desc: "Monitor vocabulary growth, Can-Do skill mastery, and exportable teacher reports.",
        },
      ],
    },
    modulesGrid: [
      {
        title: "AI Malay Tutor (Cikgu Alin)",
        desc: "Interactive conversation practice and personalized Standard Bahasa Melayu guidance.",
        bullets: [
          "Standard Bahasa Melayu conversation sessions",
          "Kid-friendly pronunciation & grammar feedback",
          "Contextual real-life conversational scenarios",
        ],
      },
      {
        title: "Unit 5 Learning",
        desc: "Curriculum-aligned lessons including 'ialah/adalah' grammar and expressing ideas.",
        bullets: [
          "Grammar mastery ('ialah' vs 'adalah')",
          "Explaining ideas with clear supporting reasons",
          "Unit 5 expression & vocabulary reinforcement",
        ],
      },
      {
        title: "Reading Comprehension",
        desc: "Interactive reading passages, vocabulary in context, and reflection questions.",
        bullets: [
          "Leveled fiction & non-fiction passages",
          "Contextual vocabulary analysis",
          "Reflection & cognitive comprehension questions",
        ],
      },
      {
        title: "Narrative Writing",
        desc: "AI-assisted story planning for character, setting, conflict, and plot sequence.",
        bullets: [
          "Main and supporting character planning",
          "Setting time, place & mood development",
          "Conflict progression & story climax",
        ],
      },
    ],
    vocabulary: {
      title: "Bahasa Melayu Vocabulary & Expressions",
      subtitle: "Master words, synonyms, antonyms, and daily expressions.",
      tabs: {
        dictionary: {
          label: "Contextual Vocabulary",
          text: "Learn Standard Bahasa Melayu word meanings with clear contextual examples.",
        },
        flashcards: {
          label: "Interactive Flashcards",
          text: "Quick-fire flashcards with audio pronunciation for long-term retention.",
        },
        wordOfDay: {
          label: "Grammar ('ialah' / 'adalah')",
          text: "Targeted grammar practice on using 'ialah' and 'adalah' correctly in sentences.",
        },
      },
    },
    testimonials: {
      title: "What Teachers & Parents Say",
      subtitle: "Success stories from schools learning Bahasa Melayu with our platform.",
      items: [
        {
          quote: "Cikgu Alin helps my students practice Bahasa Melayu conversation confidently in class.",
          author: "Cikgu Mariam Binti Ahmad",
          role: "Malay Language Teacher",
        },
        {
          quote: "The Narrative Writing module guided my child to outline story plots structurally.",
          author: "Mr. Farhan",
          role: "Parent",
        },
        {
          quote: "Can-Do skill tracking lets us monitor weekly vocabulary growth easily.",
          author: "Mrs. Zaiton",
          role: "School Administrator",
        },
      ],
    },
    safety: {
      title: "Student Safety & Privacy",
      subtitle: "A safe, child-friendly platform complying with global privacy standards.",
      items: ["COPPA & GDPR Compliant", "Child-Friendly Environment", "Data Privacy Protected"],
      body: "Student information and learning progress are fully secured. We do not sell student data.",
    },
    footer: {
      ctaTitle: "Ready to Start Learning Bahasa Melayu?",
      ctaSubtitle: "Log in now or request access for your school.",
      copyright: "Marshall Cavendish Education — Bahasa Melayu Learning Platform. All Rights Reserved.",
    },
  },
};

export const MalayLandingPage: React.FC<MalayLandingPageProps> = ({ branding }) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [lang, setLang] = useState<LangMode>("ms");
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setPublicTenantIntent({
      isMalay: true,
      language: lang,
      source: "public-route",
      landingPath: window.location.pathname,
    });
  }, [lang]);

  // Outside click & Escape key listener for Language Dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setLangDropdownOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setLangDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const text = TEXT[lang];
  const displayName = branding?.display_name || text.brandDefault;

  const scrollTo = useCallback((id: string) => {
    setNavOpen(false);
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const NAV_ANCHORS = useMemo(
    () => [
      { label: text.nav.how, id: "how-it-works" },
      { label: text.nav.modules, id: "modules" },
      { label: text.nav.tutor, id: "cikgu-ain" },
      { label: text.nav.writing, id: "writing" },
      { label: text.nav.progress, id: "progress" },
      { label: text.nav.teachers, id: "teachers" },
      { label: text.nav.safety, id: "safety" },
    ],
    [text]
  );

  const HOW_IT_WORKS_STEPS: StepItem[] = useMemo(
    () =>
      text.how.steps.map((s, idx) => ({
        step: s.step,
        title: s.title,
        description: s.desc,
        icon: idx === 0 ? BookOpen : idx === 1 ? Brain : idx === 2 ? PenLine : Target,
      })),
    [text]
  );

  const MODULES_GRID: IconGridItem[] = useMemo(
    () =>
      text.modulesGrid.map((m, idx) => ({
        icon: idx === 0 ? Brain : idx === 1 ? BookOpen : idx === 2 ? BookMarked : PenLine,
        title: m.title,
        description: m.desc,
        bullets: m.bullets,
      })),
    [text]
  );

  const VOCAB_TABS: TabItem[] = useMemo(
    () => [
      {
        id: "dictionary",
        label: text.vocabulary.tabs.dictionary.label,
        content: (
          <div className="space-y-4 text-left">
            <p className="text-gray-700 dark:text-gray-300 text-sm font-medium leading-relaxed">
              {text.vocabulary.tabs.dictionary.text}
            </p>
            <div className="rounded-2xl bg-orange-50/80 dark:bg-orange-950/40 p-4 border border-orange-200/70 dark:border-orange-900/40">
              <div className="text-xs font-bold text-orange-700 dark:text-orange-300 uppercase tracking-wider mb-1">
                Contoh Ayat:
              </div>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                "Cikgu Alin menyampaikan pengajaran Bahasa Melayu Baku dengan penuh dedikasi."
              </p>
            </div>
          </div>
        ),
      },
      {
        id: "flashcards",
        label: text.vocabulary.tabs.flashcards.label,
        content: (
          <div className="space-y-4 text-left">
            <p className="text-gray-700 dark:text-gray-300 text-sm font-medium leading-relaxed">
              {text.vocabulary.tabs.flashcards.text}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-orange-50 dark:bg-gray-800 p-3 text-center border border-orange-100 dark:border-gray-700">
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">Kata / Frasa</div>
                <div className="text-sm font-extrabold text-orange-600 dark:text-orange-400 mt-1">Dedikasi</div>
              </div>
              <div className="rounded-xl bg-orange-50 dark:bg-gray-800 p-3 text-center border border-orange-100 dark:border-gray-700">
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">Maksud</div>
                <div className="text-sm font-bold text-gray-800 dark:text-gray-200 mt-1">Pengabdian diri</div>
              </div>
            </div>
          </div>
        ),
      },
      {
        id: "wordOfDay",
        label: text.vocabulary.tabs.wordOfDay.label,
        content: (
          <div className="space-y-4 text-left">
            <p className="text-gray-700 dark:text-gray-300 text-sm font-medium leading-relaxed">
              {text.vocabulary.tabs.wordOfDay.text}
            </p>
            <div className="rounded-2xl bg-orange-500/10 p-4 border border-orange-500/20">
              <div className="text-xs font-bold text-orange-700 dark:text-orange-300 uppercase tracking-wider mb-1">
                Panduan Tatabahasa Unit 5:
              </div>
              <p className="text-xs sm:text-sm text-gray-800 dark:text-gray-200 font-medium leading-relaxed">
                Gunakan <strong>'ialah'</strong> sebelum frasa nama. Gunakan <strong>'adalah'</strong> sebelum frasa sendi nama atau frasa adjektif.
              </p>
            </div>
          </div>
        ),
      },
    ],
    [text]
  );

  const TESTIMONIALS: TestimonialItem[] = useMemo(
    () =>
      text.testimonials.items.map((item) => ({
        quote: item.quote,
        author: item.author,
        role: item.role,
      })),
    [text]
  );

  return (
    <div id="top" className="min-h-screen w-full text-gray-900 dark:text-gray-100 bg-amber-50/40 dark:bg-gray-950 font-sans">
      {/* 1. Header Navigation Bar */}
      <nav className="sticky top-0 z-50 border-b border-orange-200/50 dark:border-gray-800 bg-amber-50/95 dark:bg-gray-900/95 backdrop-blur shadow-sm">
        <div className="w-full flex items-center justify-between gap-2 xl:gap-4 px-4 sm:px-8 lg:px-12 h-[76px]">
          {/* Brand Logo & Name */}
          <button
            type="button"
            onClick={() => scrollTo("top")}
            className="flex items-center gap-3 rounded-xl py-1 hover:bg-orange-100/60 dark:hover:bg-gray-800 transition-colors shrink-0"
            aria-label="Marshall Cavendish Education"
          >
            <span className="relative flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-full bg-white ring-2 ring-orange-500/25 shadow-sm shrink-0 p-1.5 overflow-hidden">
              <img src="/images/mce-logo.png" alt="Marshall Cavendish Logo" className="h-full w-full object-contain object-center" draggable={false} />
            </span>
            <div className="hidden md:block leading-tight text-left">
              <div className="text-sm xl:text-base font-extrabold text-gray-900 dark:text-white tracking-tight">{displayName}</div>
              <div className="text-[10px] xl:text-xs font-semibold text-orange-600 dark:text-orange-400">{text.brandSubtitle}</div>
            </div>
          </button>

          {/* Desktop Center Navigation Anchors */}
          <div className="hidden lg:flex items-center justify-center gap-1 xl:gap-2">
            {NAV_ANCHORS.map((item) => (
              <button key={item.id} type="button" onClick={() => scrollTo(item.id)} className="rounded-lg px-2 py-1 text-xs xl:text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-orange-100/60 dark:hover:bg-gray-800 hover:text-orange-600 dark:hover:text-orange-400 transition-colors whitespace-nowrap">
                {item.label}
              </button>
            ))}
          </div>

          {/* Right Controls: Compact Dropdown Language Selector + Theme + Auth */}
          <div className="flex items-center justify-end gap-1.5 xl:gap-2 shrink-0">
            {/* Compact Language Dropdown Selector with outside click listener */}
            <div className="relative" ref={langDropdownRef} data-testid="malay-landing-language-selector">
              <button
                type="button"
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="flex items-center gap-1.5 rounded-full bg-orange-100/80 dark:bg-gray-800 px-3 py-1.5 text-xs font-bold text-gray-800 dark:text-gray-200 border border-orange-200/70 dark:border-gray-700 hover:bg-orange-200/70 dark:hover:bg-gray-700 transition-all shadow-sm"
                aria-label={`Language: ${malayLandingLanguageFullLabel(lang)}`}
                title={malayLandingLanguageFullLabel(lang)}
                data-testid="malay-landing-language-trigger"
              >
                <Globe className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
                <span data-testid="malay-landing-language-trigger-label">
                  {malayLandingLanguageDisplayCode(lang)}
                </span>
                <ChevronDown className={`h-3.5 w-3.5 text-gray-500 transition-transform ${langDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {langDropdownOpen ? (
                <div
                  className="absolute right-0 mt-1.5 w-auto min-w-[5.5rem] rounded-2xl bg-white dark:bg-gray-900 shadow-xl border border-orange-100 dark:border-gray-800 py-1.5 z-50"
                  data-testid="malay-landing-language-menu"
                >
                  {MALAY_LANDING_LANGUAGE_OPTIONS.map((option) => (
                    <button
                      key={option.code}
                      type="button"
                      onClick={() => {
                        setLang(option.code);
                        setLangDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs font-bold flex items-center justify-between transition-colors ${
                        lang === option.code
                          ? "text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-gray-800"
                          : "text-gray-700 dark:text-gray-300 hover:bg-orange-50 dark:hover:bg-gray-800"
                      }`}
                      aria-label={option.fullLabel}
                      title={option.fullLabel}
                      data-testid={`malay-landing-language-option-${option.code}`}
                    >
                      <span>{option.shortLabel}</span>
                      {lang === option.code && <Check className="h-3.5 w-3.5 text-orange-500" />}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {/* Theme Toggle */}
            <button
              type="button"
              onClick={toggleTheme}
              className="p-1.5 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-orange-100/60 dark:hover:bg-gray-800"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5 text-gray-700" />}
            </button>

            {/* Admin Login */}
            <Button
              variant="ghost"
              size="sm"
              className="hidden sm:inline-flex text-xs font-semibold text-gray-700 hover:text-orange-600 hover:bg-orange-100/60 px-2.5"
              onClick={() => navigate(`${ROUTES.LOGIN}?mode=admin`)}
            >
              {text.adminLogin}
            </Button>

            {/* Mobile Hamburger */}
            <button
              type="button"
              onClick={() => setNavOpen(true)}
              className="lg:hidden p-2 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-orange-100/60 dark:hover:bg-gray-800"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer Navigation */}
      {navOpen ? (
        <>
          <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setNavOpen(false)} aria-hidden />
          <div className="fixed top-0 right-0 bottom-0 z-50 w-72 max-w-[85vw] bg-white dark:bg-gray-900 border-l border-orange-100 dark:border-gray-800 shadow-2xl lg:hidden flex flex-col py-6 px-4 overflow-y-auto">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white ring-1 ring-orange-500/25 p-1 overflow-hidden">
                  <img src="/images/mce-logo.png" alt="MCE Logo" className="h-full w-full object-contain object-center" />
                </span>
                <span className="font-extrabold text-orange-600 dark:text-orange-400 text-xs">
                  {displayName}
                </span>
              </div>
              <button type="button" onClick={() => setNavOpen(false)} className="p-2 rounded-lg" aria-label="Close menu">
                <Globe className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="flex flex-col py-4 gap-1 flex-1">
              {NAV_ANCHORS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollTo(item.id)}
                  className="text-left px-4 py-3 rounded-xl font-semibold text-sm text-gray-800 dark:text-gray-200 hover:bg-orange-50 dark:hover:bg-gray-800 hover:text-orange-600"
                >
                  {item.label}
                </button>
              ))}
              <div className="pt-4 mt-2 border-t border-gray-100 dark:border-gray-800 flex flex-col gap-2.5">
                <Button
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-md shadow-orange-500/20"
                  onClick={() => navigate(`${ROUTES.LOGIN}?mode=user`)}
                >
                  {text.login}
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-orange-200 text-orange-700 hover:bg-orange-50"
                  onClick={() => navigate(ROUTES.REGISTER)}
                >
                  {text.request}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-xs text-gray-600"
                  onClick={() => navigate(`${ROUTES.LOGIN}?mode=admin`)}
                >
                  {text.adminLogin}
                </Button>
              </div>
            </div>
          </div>
        </>
      ) : null}

      {/* 2. Hero Section (Orange Education Theme) */}
      <section className="relative overflow-hidden pt-12 pb-20 lg:pt-20 lg:pb-24 bg-amber-50/40 dark:bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 rounded-full bg-orange-100/90 dark:bg-orange-950/80 px-4 py-1.5 text-xs font-extrabold text-orange-700 dark:text-orange-300 ring-1 ring-orange-500/20 shadow-sm">
                <Sparkles className="h-4 w-4 text-orange-500" />
                <span>Pembelajaran Bahasa Melayu Berteraskan AI</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight tracking-tight">
                {text.heroTitle}
              </h1>

              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto lg:mx-0 font-medium">
                {text.heroSubtitle}
              </p>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 pt-2">
                <Button
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-3.5 rounded-full shadow-lg shadow-orange-500/25 transition-all transform hover:-translate-y-0.5"
                  onClick={() => navigate(`${ROUTES.LOGIN}?mode=user`)}
                >
                  {text.login} <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="pt-8 border-t border-orange-200/60 dark:border-gray-800 grid grid-cols-3 gap-4 max-w-lg mx-auto lg:mx-0 text-left">
                <div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
                    <span>{text.trust.item1}</span>
                  </div>
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
                    <span>{text.trust.item2}</span>
                  </div>
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
                    <span>{text.trust.item3}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Card Preview */}
            <div className="lg:col-span-5">
              <div className="relative mx-auto max-w-md rounded-3xl border border-orange-200/80 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl shadow-orange-500/10">
                <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-2xl bg-orange-500 flex items-center justify-center text-white font-bold shadow-md shadow-orange-500/30">
                      <Brain className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 dark:text-white">Cikgu Alin</div>
                      <div className="text-xs text-orange-600 dark:text-orange-400 font-medium">Tutor AI Bahasa Melayu</div>
                    </div>
                  </div>
                  <span className="rounded-full bg-orange-100 dark:bg-orange-950 px-2.5 py-1 text-xs font-extrabold text-orange-700 dark:text-orange-300">
                    Aktif
                  </span>
                </div>

                <div className="space-y-3">
                  <div className="rounded-2xl bg-orange-50/70 dark:bg-orange-950/40 p-4 border border-orange-100 dark:border-orange-900/40">
                    <div className="text-xs font-semibold text-orange-700 dark:text-orange-300 mb-1">
                      {lang === "ms" ? "Soalan Unit 5:" : "Unit 5 Question:"}
                    </div>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      "Bolehkah kamu terangkan penggunaan kata pemeri <strong>'ialah'</strong> dalam ayat berikut?"
                    </p>
                  </div>

                  <div className="rounded-2xl bg-gray-50 dark:bg-gray-800/80 p-4 border border-gray-200 dark:border-gray-700 ml-4">
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                      {lang === "ms" ? "Jawapan Murid:" : "Student Answer:"}
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      "Encik Ahmad <strong>ialah</strong> guru Bahasa Melayu kami."
                    </p>
                  </div>

                  <div className="rounded-2xl bg-orange-500/10 p-3.5 border border-orange-500/20 flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
                    <div className="text-xs text-gray-700 dark:text-gray-300">
                      <span className="font-bold text-orange-600 dark:text-orange-400">
                        {lang === "ms" ? "Maklum Balas Cikgu Alin:" : "Cikgu Alin Feedback:"}
                      </span>{" "}
                      Syabas! Penggunaan 'ialah' sebelum frasa nama 'guru Bahasa Melayu' adalah tepat sekali.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. How It Works Section */}
      <div id="how-it-works" className="py-16 sm:py-24 bg-amber-50/40 dark:bg-gray-950">
        <Section title={text.how.title} subtitle={text.how.subtitle} noBorder>
          <StepTimeline steps={HOW_IT_WORKS_STEPS} variant="orange" />
        </Section>
      </div>

      {/* 4. Core Modules Grid */}
      <div id="modules" className="py-16 sm:py-24 bg-amber-50/40 dark:bg-gray-950">
        <Section
          title={lang === "ms" ? "Modul Pembelajaran Utama Bahasa Melayu" : "Core Bahasa Melayu Modules"}
          subtitle={
            lang === "ms"
              ? "Empat teras pembelajaran berkesan: Tutor AI, Unit 5, Pemahaman Membaca, dan Penulisan Naratif."
              : "Four key learning pillars: AI Tutor, Unit 5, Reading Comprehension, and Narrative Writing."
          }
          noBorder
        >
          <IconGrid items={MODULES_GRID} variant="orange" />
        </Section>
      </div>

      {/* 5. Cikgu Alin Showcase */}
      <div id="cikgu-ain" className="py-16 sm:py-24 bg-amber-50/40 dark:bg-gray-950">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SplitFeature
            visual={
              <div className="rounded-3xl border border-orange-500/20 bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 p-8 text-white shadow-xl shadow-orange-500/20">
                <Brain className="h-12 w-12 text-orange-100 mb-4" />
                <h3 className="text-2xl font-bold mb-2">
                  {lang === "ms" ? "Interaksi Perbualan Pintar" : "Smart Conversational Interaction"}
                </h3>
                <p className="text-orange-100 text-sm mb-6">
                  {lang === "ms"
                    ? "Murid boleh bercakap atau menaip soalan. Cikgu Alin memberikan jawapan yang jelas mengikut tahap murid."
                    : "Students can speak or type questions. Cikgu Alin responds clearly tailored to the student's level."}
                </p>
                <div className="rounded-2xl bg-white/15 backdrop-blur p-4 border border-white/20">
                  <div className="text-xs font-semibold text-orange-100">
                    {lang === "ms" ? "Ciri Utama:" : "Key Features:"}
                  </div>
                  <div className="text-sm font-bold mt-1">
                    {lang === "ms" ? "Latihan Sebutan, Kata Pemeri & Ungkapan" : "Pronunciation, Grammar & Expressions"}
                  </div>
                </div>
              </div>
            }
          >
            <div className="space-y-6">
              <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                {lang === "ms" ? "Cikgu Alin — Tutor AI Bahasa Melayu 24/7" : "Cikgu Alin — 24/7 AI Bahasa Melayu Tutor"}
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {lang === "ms"
                  ? "Bimbingan interaktif individu untuk murid mempertingkatkan perbualan dan tatabahasa."
                  : "Personalised interactive guidance for students to master conversation and grammar."}
              </p>
              <ul className="space-y-3">
                {[
                  lang === "ms" ? "Latihan perbualan senario harian" : "Real-life conversational scenario practice",
                  lang === "ms" ? "Maklum balas suara & tatabahasa serta-merta" : "Instant voice and grammar feedback",
                  lang === "ms" ? "Bimbingan mesra membina keyakinan murid" : "Kid-friendly guidance building student confidence",
                ].map((bullet, idx) => (
                  <li key={idx} className="flex items-center gap-2.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          </SplitFeature>
        </div>
      </div>

      {/* 6. Narrative Writing & Reading Showcase */}
      <div id="writing" className="py-16 sm:py-24 bg-amber-50/40 dark:bg-gray-950">
        <Section
          title={lang === "ms" ? "Penulisan Naratif & Pemahaman Membaca" : "Narrative Writing & Reading Comprehension"}
          subtitle={
            lang === "ms"
              ? "Bantu murid menguasai struktur karangan dan pemahaman petikan secara terancang."
              : "Helping students master essay structures and passage comprehension step-by-step."
          }
          noBorder
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="rounded-3xl border border-orange-200/70 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="h-12 w-12 rounded-2xl bg-orange-100 dark:bg-orange-950 flex items-center justify-center text-orange-600 dark:text-orange-400 mb-6 font-bold">
                <PenLine className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                {lang === "ms" ? "Penulisan Naratif (Writing)" : "Narrative Writing"}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                {lang === "ms" ? "Bimbingan langkah demi langkah untuk merancang plot karangan:" : "Step-by-step guidance for story planning:"}
              </p>
              <ul className="space-y-2.5 text-sm text-gray-700 dark:text-gray-300 font-medium">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
                  {lang === "ms" ? "Perancangan watak utama & watak sampingan" : "Main and supporting character planning"}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
                  {lang === "ms" ? "Penetapan latar tempat, masa & suasana" : "Setting place, time & mood development"}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
                  {lang === "ms" ? "Perkembangan konflik & klimaks cerita" : "Conflict progression & story climax"}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
                  {lang === "ms" ? "Semakan tatabahasa & cadangan perenggan" : "Grammar check & paragraph suggestions"}
                </li>
              </ul>
            </div>

            <div className="rounded-3xl border border-orange-200/70 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="h-12 w-12 rounded-2xl bg-orange-100 dark:bg-orange-950 flex items-center justify-center text-orange-600 dark:text-orange-400 mb-6 font-bold">
                <BookMarked className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                {lang === "ms" ? "Pemahaman Membaca (Reading)" : "Reading Comprehension"}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                {lang === "ms" ? "Petikan interaktif berserta soalan pemahaman dan refleksi:" : "Interactive passages with comprehension and reflection questions:"}
              </p>
              <ul className="space-y-2.5 text-sm text-gray-700 dark:text-gray-300 font-medium">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
                  {lang === "ms" ? "Petikan pelbagai topik & tahap" : "Leveled fiction & non-fiction passages"}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
                  {lang === "ms" ? "Pengecaman kosa kata berasaskan konteks" : "Contextual vocabulary recognition"}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
                  {lang === "ms" ? "Latihan perbandingan & refleksi idea" : "Comparison & reflection exercise"}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
                  {lang === "ms" ? "Audio bacaan & bantuan sebutan" : "Audio playback & pronunciation aid"}
                </li>
              </ul>
            </div>
          </div>
        </Section>
      </div>

      {/* 7. Progress & Vocabulary Tabs */}
      <div id="progress" className="py-16 sm:py-24 bg-amber-50/40 dark:bg-gray-950">
        <Section title={text.vocabulary.title} subtitle={text.vocabulary.subtitle} noBorder>
          <Tabs tabs={VOCAB_TABS} colorScheme="orange" />
        </Section>
      </div>

      {/* 8. Teacher & Admin Value Section */}
      <div id="teachers" className="py-16 sm:py-24 bg-amber-50/40 dark:bg-gray-950">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SplitFeature
            visual={
              <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100 dark:border-gray-800">
                  <div className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Users className="h-5 w-5 text-orange-500" />
                    {lang === "ms" ? "Ringkasan Kelas Guru" : "Teacher Class Overview"}
                  </div>
                  <span className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300 px-2.5 py-1 rounded-full font-bold">
                    {lang === "ms" ? "Aktif" : "Active"}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <MetricCard label={lang === "ms" ? "Murid" : "Students"} value={32} />
                  <MetricCard label={lang === "ms" ? "Sesi AI" : "AI Sessions"} value={148} />
                  <MetricCard label={lang === "ms" ? "Kemajuan" : "Progress"} value={86} suffix="%" />
                </div>
                <div className="rounded-2xl bg-orange-50/80 dark:bg-orange-950/30 p-4 border border-orange-100 dark:border-orange-900/30">
                  <div className="text-xs font-semibold text-orange-700 dark:text-orange-300">
                    {lang === "ms" ? "Aktiviti Terkini:" : "Recent Activity:"}
                  </div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-1">
                    {lang === "ms"
                      ? "85% murid telah menyempurnakan tugasan Unit 5 minggu ini."
                      : "85% of students completed this week's Unit 5 assignment."}
                  </div>
                </div>
              </div>
            }
          >
            <div className="space-y-6">
              <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                {lang === "ms" ? "Dashboard Guru & Laporan Kemajuan" : "Teacher Dashboard & Progress Reports"}
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {lang === "ms"
                  ? "Alatan lengkap untuk guru mengurus tugasan, memantau kemajuan murid, dan menjana laporan sekolah."
                  : "Complete tools for teachers to assign tasks, track student growth, and generate reports."}
              </p>
              <ul className="space-y-3">
                {[
                  lang === "ms" ? "Tugasan terancang mengikut tajuk & gred" : "Planned assignments by topic & grade",
                  lang === "ms" ? "Pantau penguasaan Can-Do & kosa kata murid" : "Track Can-Do skill & vocabulary growth",
                  lang === "ms" ? "Laporan bersedia dieksport untuk pentadbir & ibu bapa" : "Exportable reports for admins & parents",
                ].map((bullet, idx) => (
                  <li key={idx} className="flex items-center gap-2.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="h-4 w-4 text-orange-500 shrink-0" />
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          </SplitFeature>
        </div>
      </div>

      {/* 9. Testimonials Slider */}
      <div className="py-16 sm:py-24 bg-amber-50/40 dark:bg-gray-950">
        <Section title={text.testimonials.title} subtitle={text.testimonials.subtitle} noBorder>
          <TestimonialSlider items={TESTIMONIALS} />
        </Section>
      </div>

      {/* 10. Safety & Privacy Section */}
      <div id="safety" className="py-16 sm:py-24 bg-amber-50/40 dark:bg-gray-950">
        <Section title={text.safety.title} subtitle={text.safety.subtitle} noBorder>
          <div className="max-w-3xl mx-auto rounded-3xl border border-orange-200/70 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 text-center shadow-sm">
            <Shield className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <p className="text-gray-700 dark:text-gray-300 text-base mb-6 font-medium">
              {text.safety.body}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {text.safety.items.map((item, idx) => (
                <span
                  key={idx}
                  className="rounded-full bg-orange-100/80 dark:bg-orange-950 px-4 py-2 text-sm font-bold text-orange-800 dark:text-orange-300 border border-orange-200/60"
                >
                  ✓ {item}
                </span>
              ))}
            </div>
          </div>
        </Section>
      </div>

      {/* 11. Final CTA & Footer */}
      <footer className="bg-amber-50/40 dark:bg-gray-950 text-gray-900 dark:text-white pt-16 pb-12 border-t border-orange-200/50 dark:border-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold mb-3 text-gray-900 dark:text-white">{text.footer.ctaTitle}</h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-6 font-medium">{text.footer.ctaSubtitle}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 rounded-full shadow-lg shadow-orange-500/25"
                onClick={() => navigate(`${ROUTES.LOGIN}?mode=user`)}
              >
                {lang === "ms" ? "Mula Belajar" : "Start Learning"}
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-orange-200 dark:border-gray-700 text-orange-700 dark:text-gray-300 hover:bg-orange-50 dark:hover:bg-gray-800 rounded-full font-bold px-7"
                onClick={() => navigate(`${ROUTES.LOGIN}?mode=admin`)}
              >
                {lang === "ms" ? "Log Masuk Pentadbir" : "Admin Login"}
              </Button>
            </div>
          </div>

          <div className="pt-8 border-t border-orange-200/60 dark:border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-3">
              <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white ring-1 ring-orange-500/25 p-1 overflow-hidden">
                <img src="/images/mce-logo.png" alt="Marshall Cavendish Logo" className="h-full w-full object-contain object-center" />
              </span>
              <span className="font-extrabold text-gray-800 dark:text-gray-200">{displayName}</span>
            </div>
            <div>{text.footer.copyright}</div>
          </div>
        </div>
      </footer>
    </div>
  );
};
